import { BehaviorSubject } from 'rxjs';
import { endOfDay, startOfDay, subDays } from 'date-fns';
import { ObservableAndState } from '../utils/observable_and_state';

export type PresetOption = 'last30' | 'last7' | 'yesterday' | 'today' | null;

export interface DateRange {
  from: Date;
  to: Date;
}

export interface DateFilterState {
  range: DateRange;
  comparison?: DateRange;
  selectedPreset: PresetOption;
}

export class DateFilterService {
  private readonly dateFilterState$: BehaviorSubject<DateFilterState>;

  constructor() {
    const yesterday = subDays(new Date(), 1);
    const defaultRange: DateRange = {
      from: startOfDay(subDays(yesterday, 6)),
      to: endOfDay(yesterday),
    };
    const defaultComparison: DateRange = {
      from: startOfDay(subDays(yesterday, 13)),
      to: endOfDay(subDays(yesterday, 7)),
    };

    this.dateFilterState$ = new BehaviorSubject<DateFilterState>({
      range: defaultRange,
      comparison: defaultComparison,
      selectedPreset: 'last7',
    });
  }

  public getDateFilterState$(): ObservableAndState<DateFilterState> {
    return {
      observable: this.dateFilterState$.asObservable(),
      state: this.dateFilterState$.getValue(),
    };
  }

  public setRange(range: DateRange): void {
    const currentState = this.dateFilterState$.getValue();
    this.dateFilterState$.next({
      ...currentState,
      range,
      selectedPreset: null,
    });
  }

  public setComparison(comparison?: DateRange): void {
    const currentState = this.dateFilterState$.getValue();
    this.dateFilterState$.next({
      ...currentState,
      comparison,
    });
  }

  public setSelectedPreset(preset: PresetOption): void {
    const currentState = this.dateFilterState$.getValue();
    if (!preset) {
      this.dateFilterState$.next({
        ...currentState,
        selectedPreset: null,
      });
      return;
    }
    const now = new Date();
    const yesterday = subDays(now, 1);
    let from: Date;
    let to: Date = endOfDay(yesterday);
    switch (preset) {
      case 'last30':
        from = startOfDay(subDays(to, 29));
        break;
      case 'last7':
        from = startOfDay(subDays(to, 6));
        break;
      case 'yesterday':
        from = startOfDay(yesterday);
        break;
      case 'today':
        from = startOfDay(now);
        to = endOfDay(now);
        break;
      default:
        return;
    }
    this.dateFilterState$.next({
      ...currentState,
      range: { from, to },
      selectedPreset: preset,
    });
  }
}
